import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Button } from 'app/components/ui/button';
import { useToast } from 'app/components/ui/use-toast';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from 'app/components/ui/form';
import { useLoginSlice } from '../slice';

interface Props {
  className?: string;
}

export const ForgotPasswordForm = (props: Props) => {
  const form = useForm();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { useForgotPasswordMutation } = useLoginSlice();
  const [
    forgotPassword,
    {
      isLoading: isForgotPasswordLoading,
      isError: isForgotPasswordError,
      error: forgotPasswordError,
      data: forgotPasswordData,
    },
  ] = useForgotPasswordMutation();

  const handleForgotPassword = async () => {
    const email = form.getValues('email');
    if (!email) {
      toast({
        variant: 'destructive',
        description: 'Please enter your email address.',
      });
    } else {
      forgotPassword({ email });
    }
  };

  useEffect(() => {
    if (isForgotPasswordError) {
      toast({
        variant: 'destructive',
        description: (forgotPasswordError as any).data?.error,
      });
    }
  }, [isForgotPasswordError, forgotPasswordError]);

  useEffect(() => {
    if (forgotPasswordData) {
      toast({
        variant: 'success',
        description: forgotPasswordData.message,
      });
      form.reset();
    }
  }, [forgotPasswordData]);

  return (
    <FormProvider {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit(handleForgotPassword)}
      >
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter your email address"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button
          type="submit"
          className="w-full mt-2"
          disabled={isForgotPasswordLoading}
        >
          {isForgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
        </Button>
        {success && (
          <div className="text-green-600 text-sm text-center">
            If this email is registered, a password reset link has been sent.
          </div>
        )}
      </form>
    </FormProvider>
  );
};
